import React, { forwardRef, useImperativeHandle, useCallback, useState, useEffect } from 'react'
import styles from './LabeledGalleryDropZone.module.scss'
import { useDropzone } from 'react-dropzone'
import { Button, Icon, PreviewImage } from 'components'
import { icons } from 'assets'
import { Cloud, FileStatus } from 'utilities'
import { Props } from './types'
import { removeCroppingTool, Label } from '../Picture/SettingIndicator'
import { GalleryImage, Image } from 'models'
import { ImageGalleryEditor } from 'components/shared'

async function asyncForEach (array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array)
  }
}

const LabeledGalleryDropZone = forwardRef(
  ({ initialImages = [], additionalProps }: Props, ref) => {
    const [images, setImages] = useState([] as GalleryImage[])
    const [indexOnFocus, setIndexOnFocus] = useState(-1)

    useEffect(() => {
      if (!initialImages || !initialImages.length) return

      setImages(initialImages.map(img => ({
        ...img,
        src: img.url,
        status: FileStatus.UPLOADED
      })))
    }, [initialImages])

    const onDrop = useCallback((files: File[]) => {
      files.forEach(currentFile => {
        const fileReader = new FileReader()

        fileReader.addEventListener('load', () => {
          Object.defineProperty(currentFile, 'name', {
            writable: true,
            value: `${Number(new Date())}_${currentFile.name}`
          })
          const image: GalleryImage = {
            file: currentFile,
            src: fileReader.result,
            status: FileStatus.LOADED,
            contentType: currentFile.type
          }
          setImages(i => [...i, image])
        }, false)

        fileReader.readAsDataURL(currentFile)
      })
    }, [])

    const removeImageAtIndex = index => {
      const imagesClone = [...images]
      imagesClone.splice(index, 1)
      setImages(imagesClone)
    }

    const saveOriginalDimensions = (
      originalWidth: number, originalHeight: number, index: number
    ) => {
      const image = images[index]
      const imageAlreadyGotDimensions = image.originalHeight && image.originalWidth
      if (imageAlreadyGotDimensions) return

      const cloneImages = [...images]
      cloneImages[index] = {
        ...image,
        originalHeight,
        originalWidth
      }
      setImages(cloneImages)
    }

    const handleRemoveCropping = (index: number) => {
      return (cropping: Label) => removeCroppingTool(cropping, index, images, setImages)
    }

    useImperativeHandle(ref, () => ({
      async handleImages () {
        await asyncForEach(
          [...images],
          async (img: GalleryImage, indx: number, imgs: GalleryImage[]) => {
            if (img.status !== FileStatus.LOADED) return

            imgs[indx].status = FileStatus.UPLOADING
            setImages([...imgs])

            const response = await Cloud.uploadFile(img.file)
            imgs[indx] = {
              ...img,
              src: response.location,
              status: FileStatus.UPLOADED
            }
            setImages([...imgs])
          })
      },
      getImages () {
        return images.map(img => ({
          ...img,
          url: img.src,
          label: ''
        } as Image))
      }
    })
    )

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: ['image/png', 'image/jpeg']
    })
    const { onClick, ...rootProps } = getRootProps()

    return (
      <div className={styles.galleryContainer}>
        <label>Images</label>

        <div
          className={isDragActive ? styles.dropzoneActive : styles.dropzone}
          {...rootProps}
        >
          <input {...getInputProps()} />

          <div className={styles.imagesContainer}>
            {!images.length && (
              <Icon
                icon={icons.camera}
                className={styles.cameraIcon}
                onClick={onClick}
              />
            )}

            <p
              style={{ background: isDragActive ? 'aliceblue' : 'unset' }}
            >
              Drop images here, or use Browse files button
            </p>

            {images.map((image, index) =>
              <PreviewImage
                key={index}
                {...image}
                openImageEditor={() => setIndexOnFocus(index)}
                removeItem={() => removeImageAtIndex(index)}
                saveOriginalDimensions={(w, h) => saveOriginalDimensions(w, h, index)}
                removeCropping={handleRemoveCropping(index)}
              />
            )}
          </div>

          <div className={styles.bttnContainer}>

            {indexOnFocus > -1 &&
              <ImageGalleryEditor
                onClose={() => setIndexOnFocus(-1)}
                images={images}
                setImages={setImages}
                indexOnFocus={indexOnFocus}
                additionalProps={additionalProps}
              />
            }

            <Button
              type='add'
              size='small'
              onClick={onClick}
              className={styles.bttn}
            >
              Browse Files
            </Button>
          </div>
        </div>
      </div>
    )
  })

export default LabeledGalleryDropZone
